import React, { useEffect, useId, useRef, useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { Botao, Dialogo } from "./ui";
import { formatarPreco, parsePreco } from "../utils/preco";
import imageDefault from "../assets/no-image-default.png";
import "../css/ProdutoFolha.css";

const LIMITE_OBSERVACAO = 140;
const QUANTIDADE_MAXIMA = 99;

/**
 * Folha do produto: fotos, descrição, observação e quantidade, com "Adicionar • R$ XX".
 * somenteLeitura (admin logado) mostra só os detalhes, sem compra.
 */
const ProdutoFolha = ({ produto, aberto, aoFechar, aoAdicionar, somenteLeitura = false }) => {
  const [quantidade, setQuantidade] = useState(1);
  const [observacao, setObservacao] = useState("");
  const [fotoAtual, setFotoAtual] = useState(0);
  const galeriaRef = useRef(null);
  const observacaoId = useId();

  // Cada produto aberto começa do zero.
  useEffect(() => {
    if (aberto) {
      setQuantidade(1);
      setObservacao("");
      setFotoAtual(0);
    }
  }, [aberto, produto?.id]);

  if (!produto) return null;

  const fotos = (Array.isArray(produto.imagem) ? produto.imagem : [produto.imagem]).filter(Boolean);
  const listaFotos = fotos.length ? fotos : [imageDefault];
  const total = parsePreco(produto.preco) * quantidade;
  const categorias = (produto.categorias || []).map((c) => c?.descricao).filter(Boolean);

  const aoRolarGaleria = () => {
    const el = galeriaRef.current;
    if (!el || !el.clientWidth) return;
    setFotoAtual(Math.round(el.scrollLeft / el.clientWidth));
  };

  const irParaFoto = (indice) => {
    const el = galeriaRef.current;
    if (el) el.scrollTo({ left: indice * el.clientWidth, behavior: "smooth" });
  };

  const adicionar = () => aoAdicionar?.(produto, { quantidade, observacao: observacao.trim() });

  const rodape = somenteLeitura ? null : (
    <div className="produto-folha__acoes">
      <div className="produto-folha__quantidade" role="group" aria-label="Quantidade">
        <button
          type="button"
          onClick={() => setQuantidade((q) => Math.max(1, q - 1))}
          disabled={quantidade <= 1}
          aria-label="Diminuir quantidade"
        >
          <FaMinus aria-hidden="true" />
        </button>
        <span aria-live="polite">{quantidade}</span>
        <button
          type="button"
          onClick={() => setQuantidade((q) => Math.min(QUANTIDADE_MAXIMA, q + 1))}
          disabled={quantidade >= QUANTIDADE_MAXIMA}
          aria-label="Aumentar quantidade"
        >
          <FaPlus aria-hidden="true" />
        </button>
      </div>
      <Botao tamanho="lg" largo onClick={adicionar} className="produto-folha__adicionar">
        <span>Adicionar</span>
        <span className="produto-folha__total">{formatarPreco(total)}</span>
      </Botao>
    </div>
  );

  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} rotulo={produto.nome} rodape={rodape} className="produto-folha">
      <div className="produto-folha__galeria-area">
        <div className="produto-folha__galeria" ref={galeriaRef} onScroll={aoRolarGaleria}>
          {listaFotos.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={listaFotos.length > 1 ? `${produto.nome}, foto ${i + 1} de ${listaFotos.length}` : produto.nome}
              className="produto-folha__foto"
              onError={(e) => {
                e.currentTarget.src = imageDefault;
              }}
            />
          ))}
        </div>
        {listaFotos.length > 1 && (
          <div className="produto-folha__pontos">
            {listaFotos.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === fotoAtual ? "ativo" : ""}
                onClick={() => irParaFoto(i)}
                aria-label={`Ver foto ${i + 1}`}
                aria-current={i === fotoAtual}
              />
            ))}
          </div>
        )}
      </div>

      <div className="produto-folha__info">
        <h2 className="produto-folha__nome">{produto.nome}</h2>
        <p className="produto-folha__preco">{formatarPreco(produto.preco)}</p>
        {produto.receita && <p className="produto-folha__descricao">{produto.receita}</p>}
        {categorias.length > 0 && (
          <ul className="produto-folha__categorias" aria-label="Categorias">
            {categorias.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        )}

        {!somenteLeitura && (
          <div className="produto-folha__observacao">
            <label htmlFor={observacaoId}>Alguma observação?</label>
            <textarea
              id={observacaoId}
              value={observacao}
              maxLength={LIMITE_OBSERVACAO}
              rows={2}
              placeholder="Ex.: sem cebola, ponto da carne, retirar o molho"
              onChange={(e) => setObservacao(e.target.value)}
            />
            <span className="produto-folha__contador">
              {observacao.length}/{LIMITE_OBSERVACAO}
            </span>
          </div>
        )}
      </div>
    </Dialogo>
  );
};

export default ProdutoFolha;
